import { useState } from 'preact/hooks'

import clsx from 'clsx'

import Icon from '@/components/snippets/icon/icon'
import { useQuickAdd, isVariantOutOfStock, getVariantSizeLabel } from 'libs/athos/hooks/useQuickAdd'
import LoaderSpin from 'libs/athos/components/LoaderSpin'

// Always-visible add control for the Compact card.
// - single variant  -> direct "Add" button
// - multiple sizes   -> "Choose size" button that expands to the size row
// - sold out         -> disabled "Sold out" label (this is the card's primary CTA)
export function CompactQuickAdd({ result, className = '' }) {
  const { isAvailable, isComingSoon, addingId, addVariant } = useQuickAdd(result)
  const [expanded, setExpanded] = useState(false)

  const variants = result?.variants ?? []
  const isMultiSize = variants.length > 1

  if (!isAvailable) {
    return (
      <div className={clsx(className, 'w-full')}>
        <span
          className="flex w-full items-center justify-center rounded-lg border border-borders-default py-0.5 text-xs font-medium uppercase tracking-0.4 font-untitled-sans text-type-disabled"
          data-ui="disabled"
        >
          Sold out
        </span>
      </div>
    )
  }

  // Checked after `isAvailable` so a Coming Soon product that has also sold
  // through still reads "Sold out" — the more actionable of the two.
  if (isComingSoon) {
    return (
      <div className={clsx(className, 'w-full')}>
        <span
          className="flex w-full items-center justify-center rounded-lg border border-borders-default py-0.5 text-xs font-medium uppercase tracking-0.4 font-untitled-sans text-type-disabled"
          data-ui="disabled"
        >
          Coming Soon
        </span>
      </div>
    )
  }

  if (!isMultiSize) {
    const variant = variants[0]
    const isAdding = addingId === variant.id
    return (
      <div className={clsx(className, 'w-full')}>
        <button
          type="button"
          className="relative flex w-full items-center justify-center bg-button-white-default rounded-full py-0.5 text-xs font-medium uppercase tracking-0.4 font-untitled-sans text-type-black-primary` hover:bg-button-black-hover"
          onClick={(event) => addVariant(event, variant)}
          disabled={isAdding}
          aria-busy={isAdding}
          aria-label="Add to bag"
        >
          <span className={isAdding ? 'opacity-50' : ''}>Add</span>
          {isAdding && <LoaderSpin wrapperClass="bg-transparent" spinClass="border-t-brand-paper" />}
        </button>
      </div>
    )
  }

  return (
    <div className={clsx(className, 'w-full relative min-h-1')}>
      {!expanded ? (
        <button
          type="button"
          className="flex w-full items-center justify-center rounded-lg bg-button-white-default py-0.5 text-xs font-medium uppercase tracking-0.4 font-untitled-sans text-type-black-primary hover:bg-transparent border border-button-white-default transition-colors"
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            setExpanded(true)
          }}
          aria-expanded={expanded}
          aria-label="Choose size"
        >
          Choose size
        </button>
      ) : (
        <div className="flex w-full flex-col flex-wrap gap-[2px] rounded-lg bg-brand-paper p-0.25 absolute bottom-0 inset-x-0">
          <button
            type="button"
            className="absolute right-0 top-0 z-[1] p-0.375 text-type-black-primary"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setExpanded(false)
            }}
            aria-label="Close size selector"
          >
            <Icon id="close" className="size-[9px] children:size-full" />
          </button>
          {variants.map((variant) => {
            const isOutOfStock = isVariantOutOfStock(variant)
            const isAdding = addingId === variant.id
            return (
              <button
                key={variant.id}
                type="button"
                className={clsx(
                  'relative flex min-w-1 flex-1 items-center justify-center px-0.375 py-0.375 text-center uppercase text-type-black-primary',
                  isOutOfStock ? 'pointer-events-none opacity-70' : 'opacity-100 hover:underline'
                )}
                onClick={async (event) => {
                  await addVariant(event, variant)
                  setExpanded(false)
                }}
                disabled={isOutOfStock || isAdding}
                aria-busy={isAdding}
                aria-label={`Add size ${getVariantSizeLabel(variant)} to bag`}
                data-ui={isOutOfStock ? 'disabled' : ''}
              >
                <span className={clsx('text-xs font-medium uppercase leading-none tracking-0.4 font-untitled-sans min-w-1', isAdding && 'opacity-0')}>
                  {getVariantSizeLabel(variant)}
                </span>
                {isAdding && <LoaderSpin wrapperClass="bg-transparent" spinClass="border-t-brand-black" />}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
